import React, { useState, useEffect } from 'react';
import './VideoKinematicsLibrary.css';
import { buildApiUrl } from '../config/api';

interface VideoAnalysis {
  id: number;
  player_id: number;
  player_name: string;
  movement_name: string;
  age_band: string;
  level: string;
  kicking_foot: string;
  overall_score: number;
  created_at: string;
}

interface VideoKinematicsLibraryProps {
  playerId: number;
  onBack: () => void;
  onViewAnalysis: (analysisId: number) => void;
}

const VideoKinematicsLibrary: React.FC<VideoKinematicsLibraryProps> = ({
  playerId,
  onBack,
  onViewAnalysis
}) => {
  const [analyses, setAnalyses] = useState<VideoAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalyses = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(buildApiUrl(`/video-kinematics/player/${playerId}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAnalyses(data.analyses || []);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.detail || 'Failed to load analyses');
        }
      } catch (err) {
        setError('Failed to load analyses. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadAnalyses();
  }, [playerId]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'score-high';
    if (score >= 60) return 'score-medium';
    return 'score-low';
  };

  if (loading) {
    return (
      <div className="video-kinematics-library">
        <div className="library-header">
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
          <h1>Video Kinematics Library</h1>
        </div>
        <div className="loading">Loading analyses...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-kinematics-library">
        <div className="library-header">
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
          <h1>Video Kinematics Library</h1>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="video-kinematics-library">
      <div className="library-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h1>Video Kinematics Library</h1>
        <p className="library-subtitle">
          {analyses.length} saved {analyses.length === 1 ? 'analysis' : 'analyses'}
        </p>
      </div>

      <div className="library-content">
        {analyses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h2>No saved analyses yet</h2>
            <p>Create a new video kinematics analysis to get started.</p>
          </div>
        ) : (
          <div className="analyses-list">
            <table className="analyses-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Movement Type</th>
                  <th>Score</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {analyses.map((analysis) => (
                  <tr key={analysis.id}>
                    <td>{formatDate(analysis.created_at)}</td>
                    <td>{analysis.movement_name}</td>
                    <td>
                      <span className={`score-badge ${getScoreColor(analysis.overall_score)}`}>
                        {analysis.overall_score.toFixed(1)}/100
                      </span>
                    </td>
                    <td>
                      <button
                        className="view-button"
                        onClick={() => onViewAnalysis(analysis.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoKinematicsLibrary;




